/**
 * Second display (registration v2 mockup frame 14, build plan S8): the monitor
 * next to the capture station. It shows the photographs of the capture that
 * just finished while the operator arms the next person. No name, no number
 * and no email on this screen: the room can see it.
 *
 * A signed-in satellite like the QR Reader, but its own browser window on the
 * second monitor: the Rig tab opens /display?job={jobId} (secondDisplayUrl)
 * and App.jsx renders this component in place of the Suite shell once that
 * window is signed in (displayRouteFromLocation).
 *
 * Reads jobs/{jobId} for the event title and the newest few docs of
 * jobs/{jobId}/captureAttempts (LATEST_ATTEMPTS, by ATTEMPTS_ORDER_FIELD) for
 * the attempt to show. An attempt carries previewUrls (one XangleCS preview
 * path per photograph, in camera order) once the bridge reports the capture
 * complete. The frames are drawn with plain <img> tags from XangleCS on the
 * capture PC (http://localhost:8091): XangleCS sends no CORS headers, so
 * nothing here fetches. The browser asks once for Local Network Access; that
 * is granted on the capture PC at load-in. A frame that does not load (the
 * request went out before the grant, XangleCS still starting) is asked for
 * again a few times before its tile is given up (Frame), and when no frame at
 * all came in, the whole grid is asked for again every FRAME_RECHECK_MS while
 * the capture is on screen (CaptureGrid), so a XangleCS started or restarted
 * after the capture brings the photographs up without a reload.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import "./rigv2.css";
import { db, collection, query, orderBy, limit, onSnapshot } from "../../../../firebase.js";
import { useFirestoreDoc } from "../../../../lib/useFirestore.js";
import { rigJobPath } from "../../../../lib/eventAccount.js";

// Where XangleCS serves the preview images on the capture PC.
export const XANGLE_BASE = "http://localhost:8091";
// Frames on screen; the rest fold into one "+n more" tile (mockup: 48 shown,
// 126 more, 174 in all on the Big Rig). 48 plus the more tile fill 7 by 7.
export const MAX_TILES = 48;
// A frame XangleCS could not serve is asked for again before its tile is
// given up: XangleCS still starting, or the browser's Local Network Access
// permission granted after the first requests went out, clears in seconds.
// The waits before each retry, in ms; their number is the retry count.
export const FRAME_RETRY_DELAYS_MS = [2000, 4000, 8000];
// The line in place of the grid when a capture is on screen and not one of
// its frames loaded after the retries.
export const FRAMES_UNAVAILABLE_TEXT = "Photographs not available yet. Check that XangleCS is running.";
// Those retries are spent in about 14 seconds, too soon for a XangleCS that
// is started, or restarted, while the capture is still on the wall. So while
// that line is up the whole grid is asked for again on this interval, which
// starts every frame's retries over. Nobody has to reload the display window.
export const FRAME_RECHECK_MS = 30000;
// How many rechecks one capture gets. 20 at 30 s is 10 minutes, inside the
// SHOW_FOR_MS window the capture is on screen for, so the display stops
// asking well before it would give up on the capture anyway.
export const MAX_FRAME_RECHECKS = 20;
// A complete capture stays on screen this long after it completed, unless the
// next capture fires first. Past it the screen waits again, so a capture from
// the rehearsal, or from before a break, is not on the wall as "just taken".
export const SHOW_FOR_MS = 15 * 60 * 1000;
// How often the clock is re-read for that window (it is not a Firestore write).
const TICK_MS = 15 * 1000;
// How many attempts the display subscribes to: the newest few, not the whole
// collection. Each attempt carries previewUrls (about 9 KB for 175 cameras),
// so by the end of a 250-capture night the collection is about 2.25 MB, read
// again on every change, to show one capture. The picker needs the newest
// attempt and anything that fired after the one on screen; the rig fires one
// at a time, so five is plenty.
export const LATEST_ATTEMPTS = 5;
// The attempt field that subscription orders by. fireCapture writes
// triggeredAt (a ms number, Date.now()) on every attempt it creates and the
// schema requires it (functions/src/services/captureFlow.ts,
// functions/src/types/ingest.ts), so no attempt drops out of the ordered
// query the way a doc missing the field would; firedAt is nullable there.
// firedMillis reads triggeredAt first as well, so the query's order is the
// picker's. A single-field orderBy needs no composite index.
export const ATTEMPTS_ORDER_FIELD = "triggeredAt";

export const DISPLAY_PATH = "/display";
export const DISPLAY_WINDOW_NAME = "nycap-second-display";
const DISPLAY_PATH_RE = /^\/display\/?$/;

// The URL the Rig tab opens for a job's second display.
export function secondDisplayUrl(jobId) {
  return `${DISPLAY_PATH}?job=${encodeURIComponent(jobId)}`;
}

// App.jsx: is this window the second display, and for which job? null when
// the path is not /display; { job: null } when it is but no job was given.
export function displayRouteFromLocation(location) {
  if (!location || !DISPLAY_PATH_RE.test(String(location.pathname ?? ""))) return null;
  const job = new URLSearchParams(location.search || "").get("job");
  const trimmed = job ? job.trim() : "";
  return { job: trimmed || null };
}

// The line under the grid: the event title from the Registration card, else
// the job's display name, else its client name.
export function eventTitleOf(job) {
  return String(job?.registration?.eventTitle || job?.displayName || job?.clientName || "").trim();
}

// The <img> src for a preview path: XangleCS on the capture PC plus the path
// the bridge recorded (/pictures/{folder}/preview/0001_0001.jpg). A full URL
// is used as is.
export function previewSrc(url) {
  const s = String(url ?? "").trim();
  if (/^https?:\/\//i.test(s)) return s;
  return `${XANGLE_BASE}${s.startsWith("/") ? "" : "/"}${s}`;
}

// The wait before retry n of a frame (1-based), in ms, or null once the
// retries are used up.
export function frameRetryDelay(n, delays = FRAME_RETRY_DELAYS_MS) {
  if (!Number.isInteger(n) || n < 1 || n > delays.length) return null;
  return delays[n - 1];
}

// The src for retry n: the same image with a cache-busting query, so the
// browser asks XangleCS again instead of replaying the failure it cached.
// Retry 0 is the first request, the src as is.
export function retrySrc(src, n) {
  if (!n) return src;
  return `${src}${src.includes("?") ? "&" : "?"}r=${n}`;
}

// Not one frame loaded: every tile shown gave up after its retries and none
// came in. The grid then gives way to FRAMES_UNAVAILABLE_TEXT.
export function noFrameLoaded(loaded, broken, shown) {
  return shown > 0 && loaded === 0 && broken >= shown;
}

// Ask XangleCS again, or stop asking? Only while that line is up (noneLoaded,
// so there is nothing on the wall to disturb) and only while cycles are left.
// cycle counts the rechecks that have run: 0 while the capture is on its first
// pass, so a capture gets max rechecks in all. The 15-minute window is not
// asked about here: past it the picker drops the capture, the grid unmounts,
// and the timer goes with it.
export function shouldRecheckFrames(noneLoaded, cycle, max = MAX_FRAME_RECHECKS) {
  return Boolean(noneLoaded) && Number.isInteger(cycle) && cycle >= 0 && cycle < max;
}

// Which ask this is for one frame, from 0: the retries inside a cycle, then
// the cycles on top of them. It is the number in the cache-busting query, so
// no two asks for the same frame share a URL and a XangleCS that came back is
// never answered out of the browser's cache of the failure. Ask 0, the first
// request of the first cycle, is the plain src.
export function frameAsk(cycle = 0, retry = 0, perCycle = FRAME_RETRY_DELAYS_MS.length + 1) {
  return cycle * perCycle + retry;
}

// Attempt times are ms numbers (functions/src/types/ingest.ts). Anything else,
// a missing field or a Firestore Timestamp from a hand edit, reads as 0.
function millis(v) {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (v && typeof v.toMillis === "function") return v.toMillis();
  return 0;
}
// When an attempt fired: the order of attempts on the rig. triggeredAt is
// written on every attempt as it is created (the field the subscription
// orders by); firedAt, the bridge's later word for the same moment, stands in
// when it is missing. Never completedAt or createdAt: an older capture whose
// files landed late must not outrank the attempt that fired after it.
function firedMillis(a) {
  return millis(a?.triggeredAt) || millis(a?.firedAt);
}
// When it completed, from completedAt alone: 0 without one.
function completedMillis(a) {
  return millis(a?.completedAt);
}
// Complete, with photographs to show, and a completedAt for the window to run
// on. Without one the attempt is not shown and the screen waits.
function showable(a) {
  return a?.state === "complete" && Array.isArray(a.previewUrls) && a.previewUrls.length > 0 && completedMillis(a) > 0;
}

/**
 * Which capture the display shows: { show, waiting }.
 *
 * The newest attempt on the rig (by the time it fired: triggeredAt, else
 * firedAt) is shown when it is complete with previewUrls and a completedAt
 * within SHOW_FOR_MS of now. Anything else on top clears the grid and the
 * screen waits: an attempt that fired and is still waiting for its files, one
 * abandoned or failed, one complete without previews or without a
 * completedAt, or a capture older than the window. The grid never falls back
 * to an earlier capture, so nobody sees someone else's photographs come back
 * after their own capture.
 */
export function pickLatestCapture(attempts, now = Date.now()) {
  const list = Array.isArray(attempts) ? attempts : [];
  let latest = null;
  for (const a of list) {
    if (!a) continue;
    if (!latest) { latest = a; continue; }
    const t = firedMillis(a), lt = firedMillis(latest);
    if (t > lt || (t === lt && String(a.id ?? "") > String(latest.id ?? ""))) latest = a;
  }
  if (!latest || !showable(latest)) return { show: null, waiting: true };
  if (Number.isFinite(now) && now - completedMillis(latest) > SHOW_FOR_MS) return { show: null, waiting: true };
  return { show: latest, waiting: false };
}

// The bounded subscription: the newest n of jobs/{jobId}/captureAttempts by
// ATTEMPTS_ORDER_FIELD, newest first.
export function latestAttemptsQuery(firestore, jobId, n = LATEST_ATTEMPTS) {
  return query(collection(firestore, "jobs", jobId, "captureAttempts"), orderBy(ATTEMPTS_ORDER_FIELD, "desc"), limit(n));
}

// Those attempts, live, in the shape useFirestoreCollection gives its docs
// ({ id, ...data }). Not that hook: it has no limit and reads the whole
// collection. Returns { attempts, error }, the error logged and surfaced the
// way the hook does it (the screen shows "Not connected.").
function useLatestAttempts(jobId) {
  const [attempts, setAttempts] = useState([]);
  const [error, setError] = useState(null);
  useEffect(() => {
    if (!jobId) return undefined;
    return onSnapshot(
      latestAttemptsQuery(db, jobId),
      (snap) => setAttempts(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => {
        console.error("[LastCaptureDisplay] captureAttempts", jobId, err);
        setError(err);
      },
    );
  }, [jobId]);
  return { attempts, error };
}

// user: the Suite profile; an event account (docs/ops/event-role.md) reads the
// job's rig view (rigJobs) for the title, never the job itself.
export default function LastCaptureDisplay({ jobId, user = null }) {
  const { data: job, error: jobError } = useFirestoreDoc(rigJobPath(user, jobId));
  const { attempts, error: attemptsError } = useLatestAttempts(jobId);
  return <LastCaptureScreen jobId={jobId} job={job} attempts={attempts} error={jobError || attemptsError} />;
}

// The screen from data in hand. The tests render this one: a server render
// runs no effects, so nothing subscribes there.
export function LastCaptureScreen({ jobId, job, attempts, error }) {
  // The SHOW_FOR_MS window runs on the clock, not on Firestore writes, so the
  // pick is redone on a slow tick.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), TICK_MS);
    return () => window.clearInterval(t);
  }, []);
  const { show } = useMemo(() => pickLatestCapture(attempts, now), [attempts, now]);
  const eventTitle = eventTitleOf(job);

  return (
    <div className="rigv2-root rigv2-display">
      {!jobId ? (
        <div className="rigv2-display-wait">
          <p className="rigv2-display-line">Open this from the Rig tab.</p>
        </div>
      ) : show ? (
        <CaptureGrid key={show.id} attempt={show} eventTitle={eventTitle} />
      ) : (
        <div className="rigv2-display-wait">
          <p className="rigv2-display-line">Waiting for the next capture.</p>
          {eventTitle && <p className="rigv2-display-event">{eventTitle}</p>}
        </div>
      )}
      {error && <p className="rigv2-display-note">Not connected.</p>}
    </div>
  );
}

// The grid of the mockup: up to MAX_TILES frames in camera order, a "+n more"
// tile for the rest, and the line under it. LastCaptureScreen keys it by the
// attempt, so a new capture starts these counts over and remounts every tile
// (a hidden broken frame comes back fresh). The counts are how the capture is
// doing on the wall: frames that loaded, and frames that gave up after their
// retries.
//
// Once every tile gave up and none loaded, the grid gives way to
// FRAMES_UNAVAILABLE_TEXT, which unmounts the frames and their retries with
// them. The cycle is what brings them back: every FRAME_RECHECK_MS, while
// that line is up, the counts go to zero and the grid is keyed afresh, so
// every tile mounts again and runs its 2, 4, 8 s retries at a XangleCS that
// may have come back in the meantime. The timer exists only while the line is
// up, so a frame that loads is the end of it, and so is the unmount when the
// capture leaves the screen.
function CaptureGrid({ attempt, eventTitle }) {
  const [loaded, setLoaded] = useState(0);
  const [broken, setBroken] = useState(0);
  const [cycle, setCycle] = useState(0);
  const shown = Math.min(attempt.previewUrls.length, MAX_TILES);
  const noneLoaded = noFrameLoaded(loaded, broken, shown);
  useEffect(() => {
    if (!shouldRecheckFrames(noneLoaded, cycle)) return undefined;
    const t = window.setTimeout(() => {
      setLoaded(0);
      setBroken(0);
      setCycle((n) => n + 1);
    }, FRAME_RECHECK_MS);
    return () => window.clearTimeout(t);
  }, [noneLoaded, cycle]);
  return (
    <CaptureGridView key={`${attempt.id}:${cycle}`} attempt={attempt} eventTitle={eventTitle} cycle={cycle} loaded={loaded} broken={broken}
      onLoad={() => setLoaded((n) => n + 1)} onBroken={() => setBroken((n) => n + 1)} />
  );
}

// The grid from data in hand: the frames, or FRAMES_UNAVAILABLE_TEXT in their
// place once every tile gave up and none loaded, with the caption either way.
// Exported for the SSR tests (the counts come from img events in the browser).
export function CaptureGridView({ attempt, eventTitle, cycle = 0, loaded = 0, broken = 0, onLoad, onBroken }) {
  const all = attempt.previewUrls;
  const shown = all.slice(0, MAX_TILES);
  const more = all.length - shown.length;
  const line = `Your photographs, just taken. ${all.length} ${all.length === 1 ? "camera" : "cameras"}.`;
  return (
    <div className="rigv2-display-screen">
      {noFrameLoaded(loaded, broken, shown.length) ? (
        <div className="rigv2-display-wait">
          <p className="rigv2-display-line">{FRAMES_UNAVAILABLE_TEXT}</p>
        </div>
      ) : (
        <div className="rigv2-display-grid">
          {shown.map((url, i) => <Frame key={`${attempt.id}:${i}`} src={previewSrc(url)} cycle={cycle} onLoad={onLoad} onBroken={onBroken} />)}
          {more > 0 && <span className="rigv2-display-more">+{more} more</span>}
        </div>
      )}
      <div className="rigv2-display-cap">
        <span className="rigv2-display-line">{line}</span>
        {eventTitle && <span className="rigv2-display-event">{eventTitle}</span>}
      </div>
    </div>
  );
}

// One photograph. All of the first MAX_TILES are on screen at once, so none is
// lazy. A frame XangleCS cannot serve is asked for again after each wait in
// FRAME_RETRY_DELAYS_MS, with a cache-busting query so the browser asks
// XangleCS instead of replaying the failure; after the last one the tile hides
// instead of showing the browser's broken-image mark, and the cell stays so
// the grid keeps its 7 by 7 shape. onLoad and onBroken report to the grid. The
// cycle is the recheck this tile belongs to (frameAsk): it keeps the query
// different from the one the last cycle already failed on.
function Frame({ src, cycle = 0, onLoad, onBroken }) {
  const [retry, setRetry] = useState(0); // retries made so far
  const [broken, setBroken] = useState(false);
  const timerRef = useRef(null);
  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);
  const onError = () => {
    if (timerRef.current) return; // a retry is already on its way
    const wait = frameRetryDelay(retry + 1);
    if (wait === null) { setBroken(true); if (onBroken) onBroken(); return; }
    timerRef.current = setTimeout(() => { timerRef.current = null; setRetry((n) => n + 1); }, wait);
  };
  if (broken) return <span className="rigv2-display-frame off" aria-hidden="true" />;
  return <img className="rigv2-display-frame" src={retrySrc(src, frameAsk(cycle, retry))} alt="" draggable={false} onLoad={onLoad} onError={onError} />;
}
